import React, { useEffect, useRef, useState } from "react";
import { FILE_OPEN_EVENT } from "../utils/openFile";
import { Btn, IconBtn } from "../ui";

// The in-app file viewer (see utils/openFile.js for why). Mounted once in
// App; opens on the FILE_OPEN_EVENT. PDFs are drawn page by page with
// pdf.js (an <iframe> shows only the first page of a PDF on iPhones);
// images are shown as they are; anything else gets a frame plus an
// "Open in browser" link. A history entry is pushed so the phone's back
// button (or an edge swipe) closes the viewer instead of leaving the app.
async function renderPdf(bytes, container, signal) {
  const pdfjsLib = await import("pdfjs-dist");
  pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdn.jsdelivr.net/npm/pdfjs-dist@" + pdfjsLib.version + "/build/pdf.worker.min.mjs";
  const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
  const width = Math.max(320, container.clientWidth - 16);
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  for (let n = 1; n <= pdf.numPages; n++) {
    if (signal.aborted) return;
    const page = await pdf.getPage(n);
    const base = page.getViewport({ scale: 1 });
    const scale = width / base.width;
    const vp = page.getViewport({ scale: scale * dpr });
    const canvas = document.createElement("canvas");
    canvas.width = Math.floor(vp.width); canvas.height = Math.floor(vp.height);
    canvas.style.width = Math.floor(vp.width / dpr) + "px"; canvas.style.height = Math.floor(vp.height / dpr) + "px";
    canvas.className = "block mx-auto mb-3 bg-white shadow-card";
    container.appendChild(canvas);
    await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
  }
}

export default function FileViewer() {
  const [file, setFile] = useState(null);   // { url, title }
  const [state, setState] = useState("idle"); // idle | loading | pdf | image | other | error
  const [pages, setPages] = useState(0);
  const bodyRef = useRef(null);
  const pushedRef = useRef(false);

  useEffect(() => {
    const onOpen = (e) => { setFile({ url: e.detail.url, title: e.detail.title || "" }); };
    window.addEventListener(FILE_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(FILE_OPEN_EVENT, onOpen);
  }, []);

  // Back button / edge swipe closes the viewer.
  useEffect(() => {
    if (!file) return undefined;
    try { window.history.pushState({ fileViewer: true }, ""); pushedRef.current = true; } catch (_e) { pushedRef.current = false; }
    const onPop = () => { pushedRef.current = false; setFile(null); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [file]);

  const close = () => {
    if (pushedRef.current) { pushedRef.current = false; window.history.back(); }
    else setFile(null);
  };

  useEffect(() => {
    if (!file) { setState("idle"); setPages(0); return undefined; }
    const ctrl = new AbortController();
    const el = bodyRef.current;
    if (el) el.innerHTML = "";
    setState("loading");
    (async () => {
      try {
        const res = await fetch(file.url, { signal: ctrl.signal });
        if (!res.ok) throw new Error("HTTP " + res.status);
        const type = (res.headers.get("content-type") || "").toLowerCase();
        const bytes = new Uint8Array(await res.arrayBuffer());
        const isPdf = type.includes("pdf") || (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46);
        if (ctrl.signal.aborted) return;
        if (isPdf) {
          setState("pdf");
          await renderPdf(bytes, bodyRef.current, ctrl.signal);
          if (!ctrl.signal.aborted) setPages(bodyRef.current ? bodyRef.current.querySelectorAll("canvas").length : 0);
        } else if (type.startsWith("image/")) {
          setState("image");
        } else {
          setState("other");
        }
      } catch (e) {
        if (!ctrl.signal.aborted) setState("error");
      }
    })();
    return () => ctrl.abort();
  }, [file]);

  if (!file) return null;
  return (
    <div className="fixed inset-0 z-[5000] bg-neutral-100 flex flex-col safe-y" role="dialog" aria-modal="true" aria-label={file.title || "Document"}>
      <div className="flex items-center gap-2 px-3 py-2 bg-white border-b border-neutral-200 shrink-0">
        <IconBtn icon="arrow_back" onClick={close} title="Back" aria-label="Back" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-neutral-800 truncate">{file.title || "Document"}</div>
          {state === "pdf" && pages > 0 && <div className="text-xs text-neutral-400">{pages} {pages === 1 ? "page" : "pages"}</div>}
          {state === "loading" && <div className="text-xs text-neutral-400">Loading…</div>}
        </div>
        <Btn variant="secondary" size="sm" icon="open_in_new" onClick={() => window.open(file.url, "_blank", "noopener,noreferrer")}>Open in browser</Btn>
        <Btn variant="primary" size="sm" onClick={close}>Close</Btn>
      </div>
      <div className="flex-1 overflow-auto p-2" style={{ WebkitOverflowScrolling: "touch" }}>
        {state === "image" && <img src={file.url} alt={file.title || "Image"} className="max-w-full mx-auto block" />}
        {state === "other" && <iframe title={file.title || "Document"} src={file.url} className="w-full h-full bg-white border-0" />}
        {state === "error" && <div className="text-center text-sm text-neutral-500 py-16">This file could not be shown here. Use "Open in browser".</div>}
        <div ref={bodyRef} />
      </div>
    </div>
  );
}
